/**
 * C-03 受注詳細（FR-27・FR-30〜34・FR-81）＝**卸コンソールの核**。
 *
 * 05 §6.1 の構成：左＝原文ビューア（FR-27・`OrderSource`）・右＝下書きフォーム＋要確認（`OrderFlags`）＋進捗タイムライン。
 *
 * ★ S10 で AI 下書きが載る＝**原文ビューア（3バリアント）・要確認の候補ボタン・要確認ハイライト**を差し込む。
 *   承認可否は**サーバの `can_approve` をそのまま使う**（FR-30）＝画面で「フラグ0件なら押せる」を書き直すと、
 *   サーバの 422 と食い違う日が来る。確定は必ず人（LC-01/02）＝AI は下書きまで。
 */

import { CHANNEL_LABEL, Badge, Card, ConfirmDialog, ErrorNote, formatDate, formatDateTime, Money, ProgressBar, Skeleton, statusTone, Table, Td } from "@kenzai/ui";
import { auth, console_, type OrderDetail as Detail } from "@kenzai/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { MOBILE_TABBAR_HEIGHT, useIsMobile } from "../useIsMobile";

import { SourceViewer } from "./OrderSource";
import { Flags, headerHasOpenFlag, isAiFailed, itemHasOpenFlag } from "./OrderFlags";
import { LINE_ALERT_TEXT, orderGrandTotal } from "./orderDisplay";

// 卸の業務語彙（LC-08 の左）＝**発注側（A-03）と同じ order_events から導出**（FR-107）
const CONSOLE_STEPS = ["未確認", "確認済", "出荷指示済", "配送中", "納品済"];

type Dialog =
  | null
  | { kind: "approve" }
  | { kind: "reject" }
  | { kind: "cancel" }
  // D-58：進行操作（出荷した／配送に出た／納品済みにする）＝いずれも M1
  | { kind: "ship" }
  | { kind: "out_for_delivery" }
  | { kind: "deliver" };

/** 納品書を出せる状態（サーバ `documents/service.ISSUABLE_STATUSES` と対）。取消は含まない。 */
const ISSUABLE = new Set(["確認済", "出荷指示済", "配送中", "納品済"]);

export function OrderDetail() {
  // 来た経路（受注管理のどのタブか）を保って戻す。履歴の state を使わないのは、
  // URL 直打ち・別タブで開いた場合に state が無く「戻れない」ためで、
  // 既定は未処理（＝卸が最も多く見る場所）にする。
  const [search] = useSearchParams();
  const backTo = search.get("from") === "all" ? "/orders" : "/orders?tab=todo";
  const isMobile = useIsMobile();
  const { orderId } = useParams();
  const id = Number(orderId);
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<Dialog>(null);
  const [error, setError] = useState<unknown>(null);

  const q = useQuery({ queryKey: ["order", id], queryFn: () => console_.order(id) });
  // 得意先の新規登録は管理者のみ（03 §7）＝要確認解消の「新しく登録」を出すかの判定に role を使う。
  const me = useQuery({ queryKey: ["me"], queryFn: () => auth.me(), staleTime: Infinity });
  const isAdmin = me.data?.role === "admin";

  const act = useMutation({
    mutationFn: async ({
      kind,
      reason,
    }: {
      kind: "approve" | "reject" | "cancel" | "ship" | "out_for_delivery" | "deliver";
      reason: string;
    }) => {
      const at = q.data!.order.updated_at;
      if (kind === "approve") return console_.approve(id, at);
      if (kind === "ship") return console_.ship(id, at);
      if (kind === "out_for_delivery") return console_.outForDelivery(id, at);
      if (kind === "deliver") return console_.deliver(id, at);
      if (kind === "reject") return console_.reject(id, at, reason);
      return console_.cancel(id, at, reason);
    },
    onSuccess: () => {
      setDialog(null);
      setError(null);
      qc.invalidateQueries({ queryKey: ["order", id] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (e) => {
      setDialog(null);
      setError(e);
    },
  });

  // 帳票の発行（FR-45）。**成功しても画面の状態は変えない**（保存しない＝再取得するものが無い）。
  const print = useMutation({
    mutationFn: () => console_.printDeliveryNote(id),
    onError: (e) => setError(e),
    onSuccess: () => setError(null),
  });

  // ★2ペイン（原文｜下書き）の**形のまま**待つ（M1.7）。読み込み中に `<table>` や
  //   testid 付きの要素を置かないこと（kit.tsx `Skeleton` のコメント）。
  if (q.isLoading) return <OrderDetailSkeleton isMobile={isMobile} />;
  if (q.isError) return <ErrorNote error={q.error} />;
  const d = q.data!;
  const openFlags = d.flags.filter((f) => f.resolved_at === null);
  const aiFailed = isAiFailed(d);

  return (
    <>
      {/* D-76：受注箱は受注管理に統合。**来た経路に戻す**（未処理から来たら未処理へ）＝
          以前は常に /inbox 固定で、すべてタブから開いても未処理に着地していた。 */}
      <Link
        to={backTo}
        style={{ color: "var(--kz-color-primary)", fontSize: "var(--kz-font-size-sm)" }}
      >
        ← 受注管理へ
      </Link>

      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--kz-space-3)",
          // 390px では h1＋バッジ3枚＋受付時刻が1行に入らない＝折り返す（nowrap のままだと溢れる）
          flexWrap: "wrap",
          margin: "var(--kz-space-3) 0 var(--kz-space-5)",
        }}
      >
        <h1 className="kz-num" style={{ fontSize: "var(--kz-font-size-xl)", margin: 0 }}>
          {d.order.display_no}
        </h1>
        <Badge tone={statusTone(d.order.status_cache)}>{d.order.status_cache}</Badge>
        <Badge>{CHANNEL_LABEL[d.order.channel] ?? d.order.channel}</Badge>
        {/* LC-01：承認前は「下書き（AI）」であることを明示＝AI が自動で確定した印象を与えない */}
        {d.order.status_cache === "未確認" && <Badge tone="neutral">AI下書き・未確定</Badge>}
        <span style={{ marginLeft: "auto", color: "var(--kz-color-ink-muted)", fontSize: "var(--kz-font-size-sm)" }}>
          <span className="kz-num">{formatDateTime(d.order.received_at)}</span> 受付
        </span>
      </header>

      <ErrorNote error={error} />

      {/* FR-130・D-89：LINE 未達。**承認・出荷を進める前に見える位置**に置く＝
          「確定の連絡をしたつもりで、相手には届いていない」を、次の操作より先に知らせる。 */}
      {d.line_alert && <LineAlertBanner detail={d} />}

      {/* C-21 への導線（05 §6.1）＝この注文について「社内に指示」「運営に発注を依頼」。
          order_id を予備充填して開く＝依頼の本文に品目・数量・希望日の要約が入る。 */}
      <div style={{ display: "flex", gap: "var(--kz-space-2)", flexWrap: "wrap", marginBottom: "var(--kz-space-4)" }}>
        {/* ★`--sm` は**表の行の中だけ**（mobile.css の 44px 復元がそこだけ除外している）。
            ここは表の外なので付けない＝スマホで 33px の押しにくい的にしない（05 §5.3）。 */}
        <Link
          to={`/requests?order_id=${id}`}
          data-testid="order-request-link"
          className="kz-btn"
        >
          担当者に指示 / 発注を運営に依頼
        </Link>
        {d.open_request_count > 0 && (
          <span style={{ alignSelf: "center", fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>
            未完了の発注の指示・依頼 {d.open_request_count} 件
          </span>
        )}
      </div>

      {/* ★スマホは1カラムで、**要確認を一番上**に置く（D-68）＝承認は「全フラグ解消まで
          disabled」なので、スマホでまず解くべきものが最初に来ないと、原文を延々スクロールして
          から要確認にたどり着くことになる。デスクトップの2ペインは並びを変えない。 */}
      {isMobile ? (
        <div style={{ display: "grid", gap: "var(--kz-space-4)" }}>
          {aiFailed && <AiFailedBanner />}
          {openFlags.length > 0 && <Flags detail={d} orderId={id} isAdmin={isAdmin} />}
          <Draft detail={d} />
          <SourceViewer detail={d} />
          <Timeline detail={d} />
        </div>
      ) : (
        <div
          style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.4fr)", gap: "var(--kz-space-4)" }}
        >
          <SourceViewer detail={d} />

          <div style={{ display: "grid", gap: "var(--kz-space-4)" }}>
            {aiFailed && <AiFailedBanner />}
            {openFlags.length > 0 && <Flags detail={d} orderId={id} isAdmin={isAdmin} />}
            <Draft detail={d} />
            <Timeline detail={d} />
          </div>
        </div>
      )}

      {/* 承認バー＝**画面の一番下でなく、常に見えるところ**（承認がこの画面の目的）。
          ★スマホでは下部タブと**同じ場所を取り合う**＝タブの高さぶん持ち上げ、z-index も
          タブ（20）より低くする。持ち上げないと承認・納品ボタンがタブに隠れて押せない
          （DOM 上は見えているので、E2E の click が「別要素が上にある」で落ちて初めて分かる）。 */}
      <div
        style={{
          position: "sticky",
          bottom: isMobile ? MOBILE_TABBAR_HEIGHT : 0,
          zIndex: isMobile ? 15 : undefined,
          marginTop: "var(--kz-space-5)",
          padding: "var(--kz-space-4)",
          background: "var(--kz-color-surface)",
          border: "1px solid var(--kz-color-border)",
          borderRadius: "var(--kz-radius-md)",
          display: "flex",
          alignItems: "center",
          gap: "var(--kz-space-3)",
          boxShadow: "var(--kz-shadow-card)",
        }}
      >
        <div style={{ fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>
          {/* FR-30：**押せない理由を書く**（disabled だけだと現場が詰まって電話が来る） */}
          {openFlags.length > 0
            ? `要確認が ${openFlags.length} 件残っています。すべて解消すると承認できます。`
            : d.can_deliver
              ? "お届けが済んだら「納品済みにする」を押してください。"
              : d.order.status_cache !== "未確認"
                ? `この注文は「${d.order.status_cache}」です。`
                : "内容をご確認のうえ承認してください。"}
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: "var(--kz-space-2)" }}>
          {d.order.status_cache === "未確認" && (
            <button type="button" onClick={() => setDialog({ kind: "reject" })} data-testid="reject" className="kz-btn">
              差し戻す
            </button>
          )}
          {/* 取消は納品済でも出す＝**API（service.cancel）が納品済を弾かない**（取消>納品済＝
              04 §3 の導出）ので UI だけで隠すと画面と API が食い違う。かつ誤 deliver 時の
              唯一の回復手段（取消）を消さない。取消済みのときだけ隠す。 */}
          {d.order.status_cache !== "取消" && (
            <button type="button" onClick={() => setDialog({ kind: "cancel" })} data-testid="cancel" className="kz-btn">
              取り消す
            </button>
          )}
          {/* 納品書の印刷（FR-45・D-76・M1.5）＝**承認済み以上・未取消のときだけ出す**。
              条件はサーバの 422 と二重防御（画面に状態機械を二重実装しない方針と同じで、
              ここは can_* を増やさず既存の status_cache で足りる＝「承認済み以上」は
              can_approve の裏返しではない〔取消も承認済み以上を通る〕ため）。
              ★POST → Blob → 新規タブ（`printHtml`）＝`window.open(url)` にすると GET になり
              CSRF の前提（GET に副作用なし）が崩れる。 */}
          {ISSUABLE.has(d.order.status_cache) && (
            <button
              type="button"
              onClick={() => print.mutate()}
              disabled={print.isPending}
              data-testid="print-delivery-note"
              className="kz-btn"
            >
              {print.isPending ? "発行しています…" : "納品書を印刷"}
            </button>
          )}
          {/* D-58：進行操作は**サーバの can_* をそのまま使う**（状態機械を画面に二重実装しない）。
              出荷→配送→納品の順に出るが、便を使わない卸もあるので飛ばしても API が許す範囲は許す。 */}
          {d.can_ship && (
            <button type="button" onClick={() => setDialog({ kind: "ship" })} data-testid="ship" className="kz-btn">
              出荷した
            </button>
          )}
          {d.can_out_for_delivery && (
            <button
              type="button"
              onClick={() => setDialog({ kind: "out_for_delivery" })}
              data-testid="out-for-delivery"
              className="kz-btn"
            >
              配送に出た
            </button>
          )}
          {/* FR-103 改（D-54①）：承認済み以上・未納品なら「納品済みにする」（サーバの can_deliver をそのまま使う） */}
          {d.can_deliver && (
            <button
              type="button"
              onClick={() => setDialog({ kind: "deliver" })}
              data-testid="deliver"
              className="kz-btn kz-btn--primary"
            >
              納品済みにする
            </button>
          )}
          <button
            type="button"
            // **サーバの判定をそのまま使う**（FR-30）＝画面で書き直さない
            disabled={!d.can_approve}
            onClick={() => setDialog({ kind: "approve" })}
            data-testid="approve"
            // 押せないときの見た目（薄く・カーソル）は `.kz-btn:disabled` が持つ＝
            // 画面ごとに「押せない色」を作らない。**黄（--accent）は使わない**＝
            // console の承認は従来どおり緑（LC-02 の黄CTA は現時点でどの画面でも未使用）。
            className="kz-btn kz-btn--primary"
          >
            {/* LC-01/02：**人の行為であることが読める文言**＝「全自動」と読めない */}
            内容を確認して承認
          </button>
        </div>
      </div>

      {dialog?.kind === "approve" && (
        <ConfirmDialog
          title="この内容で承認しますか？"
          body={
            <>
              承認すると取引先に「確定」として伝わります（
              <span className="kz-num">{d.items.length}</span> 点・
              {/* FR-131：**取引先に伝わる額**＝商品計＋配送料（表の「合計」と同じ数字にする。
                  ここだけ商品計のままだと、確認画面と明細で違う額を見せることになる） */}
              <Money value={orderGrandTotal(d.total_amount, d.order.delivery_fee)} />
              {d.order.delivery_fee !== null && (
                <>
                  ・うち配送料 <Money value={d.order.delivery_fee} />
                </>
              )}
              ）。
            </>
          }
          confirmLabel="承認する"
          busy={act.isPending}
          onConfirm={() => act.mutate({ kind: "approve", reason: "" })}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "reject" && (
        <ConfirmDialog
          title="この注文を差し戻しますか？"
          body="下書きへ戻します。理由は記録に残ります。"
          confirmLabel="差し戻す"
          requireReason
          busy={act.isPending}
          onConfirm={(reason) => act.mutate({ kind: "reject", reason })}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "cancel" && (
        <ConfirmDialog
          title="この注文を取り消しますか？"
          body={
            <>
              取り消した注文は元に戻せません。理由は記録に残ります。
              {/* ★D-69⑧：紐づく指示・依頼が残っていると、**取り消した注文の材料を
                  社内担当者や運営がそのまま発注してしまう**。件数を出して取消へ誘導する。
                  リンクはダイアログの**外**に置けないので、ここでは件数だけ言い、
                  行き先は下の案内文で示す（ダイアログ内のリンクを押すと、確認中の
                  取消操作が黙って捨てられる＝S7 の敵対レビューで直した形）。 */}
              {d.open_request_count > 0 && (
                <>
                  <br />
                  <strong data-testid="open-requests-warning">
                    この注文には未完了の発注の指示・依頼が {d.open_request_count} 件あります。
                  </strong>
                  <br />
                  取り消したあと、「発注管理」の画面でその依頼も取り消してください。
                </>
              )}
            </>
          }
          confirmLabel="取り消す"
          tone="danger"
          requireReason
          busy={act.isPending}
          onConfirm={(reason) => act.mutate({ kind: "cancel", reason })}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "ship" && (
        <ConfirmDialog
          title="この注文を出荷済みにしますか？"
          body="出荷したことを記録します。取引先には「出荷しました」の通知が届きます。"
          confirmLabel="出荷した"
          busy={act.isPending}
          onConfirm={() => act.mutate({ kind: "ship", reason: "" })}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "out_for_delivery" && (
        <ConfirmDialog
          title="この注文を配送中にしますか？"
          body="配送に出たことを記録します。取引先には「配送中です」の通知が届きます。"
          confirmLabel="配送に出た"
          busy={act.isPending}
          onConfirm={() => act.mutate({ kind: "out_for_delivery", reason: "" })}
          onCancel={() => setDialog(null)}
        />
      )}
      {dialog?.kind === "deliver" && (
        <ConfirmDialog
          title="この注文を納品済みにしますか？"
          body="お届け済みとして記録します。取引先には「お届けしました」の通知が届きます。"
          confirmLabel="納品済みにする"
          busy={act.isPending}
          onConfirm={() => act.mutate({ kind: "deliver", reason: "" })}
          onCancel={() => setDialog(null)}
        />
      )}
    </>
  );
}

/**
 * フォールバックの見せ場（07 §7）＝AI が読めなかった注文。**原文（左）はそのまま届く**ので、
 * 右は空/断片＝正直に「読めませんでした」と出し、人が原文から起票する（エラーで消えない）。
 */
function AiFailedBanner() {
  return (
    <Card style={{ borderColor: "var(--kz-status-warn)", borderWidth: 2, background: "var(--kz-status-warn-weak)" }}>
      <div style={{ display: "flex", gap: "var(--kz-space-2)", alignItems: "baseline" }}>
        <Badge tone="warn">読めなかった注文</Badge>
        <div style={{ fontSize: "var(--kz-font-size-sm)" }}>
          <strong>AI が内容を読み取れませんでした。</strong>
          <span style={{ color: "var(--kz-color-ink-muted)" }}>
            {" "}
            左の原文をご確認のうえ、手で起票してください。原文は消えていません。
          </span>
        </div>
      </div>
    </Card>
  );
}

/**
 * 読み込み中（M1.7）＝**この画面の骨格**（見出し／左＝原文・右＝下書き／下＝承認バー）で待つ。
 *
 * ★データ由来の要素（表・testid 付き）は1つも置かない。E2E には「`table` が見えた＝
 *   データが確定した」を前提に本文を読む検査があり、器だけ先に出すと空の中身を読んで落ちる。
 */
function OrderDetailSkeleton({ isMobile }: { isMobile: boolean }) {
  const pane = (
    <Card>
      <Skeleton rows={1} height={18} width="35%" style={{ marginBottom: "var(--kz-space-4)" }} />
      <Skeleton rows={5} height={16} />
    </Card>
  );
  return (
    <>
      <Skeleton rows={1} height={28} width="30%" style={{ margin: "var(--kz-space-3) 0 var(--kz-space-5)" }} />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 1fr) minmax(0, 1.4fr)",
          gap: "var(--kz-space-4)",
        }}
      >
        {pane}
        {pane}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* FR-130・D-89：LINE 未達の帯と通知の履歴                              */
/* ------------------------------------------------------------------ */

/** 通知の種類（`notifications.kind`＝06 §7.1 のテンプレ5種＋取消）。**生の英字を出さない**。 */
const NOTIFY_KIND_LABEL: Record<string, string> = {
  receipt: "受付のお知らせ",
  confirmed: "確定のお知らせ",
  shipped: "出荷のお知らせ",
  out_for_delivery: "配送中のお知らせ",
  delivered: "納品のお知らせ",
  cancelled: "取消のお知らせ",
};

/** 送信状態（04 §2.5）。`skipped`＝そもそも送る相手がいない／`failed`＝送ろうとして落ちた。
 *  **どちらも自動では再送されない**（queued に戻さない設計）ので、卸に次の一手を促す必要がある。 */
const NOTIFY_STATUS: Record<string, { label: string; tone: "neutral" | "ok" | "warn" | "danger" }> = {
  queued: { label: "送信待ち", tone: "neutral" },
  sent: { label: "送信済み", tone: "ok" },
  failed: { label: "送信できませんでした", tone: "danger" },
  skipped: { label: "送信していません", tone: "warn" },
};

/** 送れなかった理由のうち、こちらで意味を確定できるもの。**それ以外は出さない**
 *  （外部エラーの生文字列を卸に見せても行動が変わらず、不安だけが残る）。 */
const NOTIFY_ERROR_LABEL: Record<string, string> = {
  no_destination: "LINE の送り先がありません（友だち追加がまだ、またはブロック中）",
};

/**
 * 未達の帯（05 §6.1 C-03）。**通知の履歴を同じ場所に出す**＝チップを見た卸が
 * 「何が届いていないのか」をこの画面だけで確かめられるようにする。
 *
 * ★履歴は**この帯の中だけ**に出す＝未達でないときに通知一覧を常設すると、
 *   受注詳細の主目的（内容を確認して承認する）から目を逸らす情報が1ブロック増える。
 */
function LineAlertBanner({ detail }: { detail: Detail }) {
  return (
    <Card
      data-testid="line-alert"
      style={{
        borderColor: "var(--kz-status-warn)",
        borderWidth: 2,
        background: "var(--kz-status-warn-weak)",
        marginBottom: "var(--kz-space-4)",
      }}
    >
      <div style={{ display: "flex", gap: "var(--kz-space-2)", alignItems: "baseline", flexWrap: "wrap" }}>
        <Badge tone="warn">LINE未達</Badge>
        <strong style={{ fontSize: "var(--kz-font-size-sm)" }}>{LINE_ALERT_TEXT}</strong>
      </div>
      <div style={{ marginTop: "var(--kz-space-3)", fontSize: "var(--kz-font-size-sm)" }}>
        <div style={{ color: "var(--kz-color-ink-muted)", marginBottom: "var(--kz-space-1)" }}>
          このご注文の通知
        </div>
        {detail.notifications.length === 0 ? (
          // 発注者がブロック中でも、まだ1通も出していない段階はある（受付直後など）
          <p style={{ margin: 0 }}>まだ通知はありません。</p>
        ) : (
          <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
            {detail.notifications.map((n, i) => {
              const s = NOTIFY_STATUS[n.status] ?? { label: n.status, tone: "neutral" as const };
              return (
                <li
                  key={`${n.kind}-${i}`}
                  data-testid={`notification-${n.kind}`}
                  style={{ display: "flex", gap: "var(--kz-space-3)", alignItems: "baseline", padding: "2px 0" }}
                >
                  <span style={{ minWidth: "11em" }}>{NOTIFY_KIND_LABEL[n.kind] ?? n.kind}</span>
                  <Badge tone={s.tone}>{s.label}</Badge>
                  <span className="kz-num" style={{ color: "var(--kz-color-ink-muted)" }}>
                    {n.sent_at ? formatDateTime(n.sent_at) : ""}
                  </span>
                  {n.error && NOTIFY_ERROR_LABEL[n.error] && (
                    <span style={{ color: "var(--kz-color-ink-muted)" }}>{NOTIFY_ERROR_LABEL[n.error]}</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Card>
  );
}

/** ヘッダの確度サマリ（design-brief §4）＝各項目に「確定（緑）/要確認（赤）」。
 *  数値の確度は API に出さない（ai_drafts のみ）＝**フラグの有無**で示す（過信を防ぐ・§2）。 */
function FieldStatus({ flagged }: { flagged: boolean }) {
  return flagged ? <Badge tone="danger">要確認</Badge> : <Badge tone="ok">確定</Badge>;
}

/** AI 下書きのヘッダ（得意先・現場・希望日・便）＝名称は既存 API から引いて表示。 */
function DraftHeader({ detail }: { detail: Detail }) {
  const { order } = detail;
  const customer = useQuery({
    queryKey: ["customer", order.customer_id],
    queryFn: () => console_.customer(order.customer_id!),
    enabled: order.customer_id !== null,
  });
  const sites = useQuery({
    queryKey: ["sites", order.customer_id],
    queryFn: () => console_.sites(order.customer_id!),
    enabled: order.customer_id !== null,
  });
  const bins = useQuery({ queryKey: ["bins"], queryFn: () => console_.bins() });

  const siteName = order.site_id != null ? sites.data?.find((s) => s.id === order.site_id)?.name : null;
  const binName = order.bin_id != null ? bins.data?.find((b) => b.id === order.bin_id)?.name : null;

  const rows: { label: string; value: ReactValue; field: string }[] = [
    { label: "得意先", value: order.customer_id != null ? customer.data?.name ?? "…" : null, field: "customer" },
    { label: "現場", value: order.site_id != null ? siteName ?? "…" : null, field: "site" },
    { label: "納品希望日", value: order.desired_date ? formatDate(order.desired_date) : null, field: "date" },
    { label: "配送便", value: order.bin_id != null ? binName ?? "…" : null, field: "bin" },
  ];

  return (
    <dl style={{ margin: 0, display: "grid", gridTemplateColumns: "auto 1fr auto", gap: "var(--kz-space-2) var(--kz-space-4)", alignItems: "center", fontSize: "var(--kz-font-size-sm)" }}>
      {rows.map((r) => {
        const flagged = headerHasOpenFlag(detail, r.field);
        return (
          <div key={r.field} style={{ display: "contents" }}>
            <dt style={{ color: "var(--kz-color-ink-muted)" }}>{r.label}</dt>
            <dd className={r.field === "date" ? "kz-num" : undefined} style={{ margin: 0, color: r.value == null ? "var(--kz-color-ink-muted)" : "var(--kz-color-ink)" }}>
              {r.value ?? "未確定"}
            </dd>
            <FieldStatus flagged={flagged} />
          </div>
        );
      })}
      {/* T-D25：会社の次に「人」。**`FieldStatus` を出さない**＝要確認フラグの対象ではないので、
          緑の「確定」を付けると「誰かが確認して確定した」という嘘になる（「状態」行と同じ扱い）。
          手入力・AI 起票は接続が無い＝**永久に埋まらない**ので「未確定」ではなく「—」。 */}
      <div style={{ display: "contents" }}>
        <dt style={{ color: "var(--kz-color-ink-muted)" }}>発注者</dt>
        <dd style={{ margin: 0, color: detail.orderer_name ? undefined : "var(--kz-color-ink-muted)" }}>
          {detail.orderer_name ? (
            <>
              {detail.orderer_name}
              {!detail.orderer_name_is_registered && (
                <span style={{ color: "var(--kz-color-ink-muted)" }}>（LINE の表示名）</span>
              )}
              {/* D-63：連絡先の卸側表示は T-D25 の担当＝ここで出さないと、職人が登録した
                  電話を卸は**どこからも見られない**（note はスクラブで伏字になる）。 */}
              {detail.orderer_phone && (
                <>
                  {" "}
                  <a href={`tel:${detail.orderer_phone}`} className="kz-num" style={{ color: "var(--kz-color-primary)" }}>
                    {detail.orderer_phone}
                  </a>
                </>
              )}
            </>
          ) : (
            "—"
          )}
        </dd>
        <span />
      </div>
      <div style={{ display: "contents" }}>
        <dt style={{ color: "var(--kz-color-ink-muted)" }}>状態</dt>
        <dd style={{ margin: 0 }}>{order.status_cache}</dd>
        <span />
      </div>
    </dl>
  );
}

type ReactValue = string | null;

/** 下書きフォーム＝明細と金額。要確認の明細は**赤くハイライト**（design-brief §5＝過信を防ぐ）。 */
function Draft({ detail }: { detail: Detail }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const orderId = detail.order.id;
  // FR-28（D-54④）：分割は**承認前（未確認）だけ**。1通に複数現場/便が混じったときの起票補助。
  const canSplit = detail.order.status_cache === "未確認" && detail.items.length > 1;
  const [splitMode, setSplitMode] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const split = useMutation({
    mutationFn: () => console_.split(orderId, detail.order.updated_at, [...selected]),
    onSuccess: (res) => {
      setConfirm(false);
      setSplitMode(false);
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ["order", orderId] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["orders"] });
      nav(`/orders/${res.new_order_id}`); // 切り出した新注文へ移動（そのまま現場/便を直せる）
    },
    onError: (e) => {
      setConfirm(false);
      setError(e);
    },
  });

  const toggle = (lineNo: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(lineNo)) next.delete(lineNo);
      else next.add(lineNo);
      return next;
    });
  };
  // 全件は選べない（一部を切り出す）＝サーバの 422 と同じ判断を画面でも出す
  const canExecuteSplit = selected.size >= 1 && selected.size < detail.items.length;

  return (
    <Card padding="0">
      <div style={{ display: "flex", alignItems: "center", padding: "var(--kz-space-4)", gap: "var(--kz-space-3)" }}>
        <h2 style={{ margin: 0, fontSize: "var(--kz-font-size-md)" }}>注文の内容</h2>
        {canSplit && !splitMode && (
          <button
            type="button"
            data-testid="split-start"
            onClick={() => setSplitMode(true)}
            className="kz-btn kz-btn--sm"
            style={{ marginLeft: "auto" }}
          >
            明細を分割
          </button>
        )}
        {splitMode && (
          <div style={{ marginLeft: "auto", display: "flex", gap: "var(--kz-space-2)", alignItems: "center" }}>
            <span style={{ fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>
              別注文にする明細を選んでください
            </span>
            <button
              type="button"
              data-testid="split-execute"
              disabled={!canExecuteSplit || split.isPending}
              onClick={() => setConfirm(true)}
              className="kz-btn kz-btn--primary kz-btn--sm"
            >
              選択を別注文に分割
            </button>
            <button
              type="button"
              onClick={() => {
                setSplitMode(false);
                setSelected(new Set());
              }}
              className="kz-btn kz-btn--sm"
            >
              やめる
            </button>
          </div>
        )}
      </div>
      <div style={{ padding: "0 var(--kz-space-4) var(--kz-space-4)" }}>
        <ErrorNote error={error} />
        <DraftHeader detail={detail} />
      </div>
      <Table head={[...(splitMode ? ["選択"] : []), "#", "品名", "数量", "単価", "金額"]}>
        {detail.items.map((i) => {
          const flagged = itemHasOpenFlag(detail, i.line_no);
          return (
            <tr
              key={i.id}
              data-testid={`item-${i.line_no}`}
              // 要確認ハイライト（design-brief §5）＝AI が自信の無い明細を赤系で目立たせる
              style={flagged ? { background: "var(--kz-status-danger-weak)" } : undefined}
            >
              {splitMode && (
                <Td>
                  <input
                    type="checkbox"
                    data-testid={`split-pick-${i.line_no}`}
                    checked={selected.has(i.line_no)}
                    onChange={() => toggle(i.line_no)}
                    style={{ width: 18, height: 18, cursor: "pointer" }}
                  />
                </Td>
              )}
              <Td>
                <span className="kz-num">{i.line_no}</span>
              </Td>
              <Td>
                {i.raw_text ?? (i.product_id ? `商品 #${i.product_id}` : "—")}
                {flagged && (
                  <span style={{ marginLeft: "var(--kz-space-2)" }}>
                    <Badge tone="danger">要確認</Badge>
                  </span>
                )}
              </Td>
              <Td style={{ whiteSpace: "nowrap" }}>
                {i.qty === null ? (
                  // FR-26：**読み取れない数量は推測しない**＝空欄のまま人へ渡す
                  <span style={{ color: "var(--kz-status-warn)" }}>読み取れません</span>
                ) : (
                  <>
                    <span className="kz-num">{Number(i.qty)}</span> {i.unit}
                  </>
                )}
              </Td>
              <Td style={{ whiteSpace: "nowrap" }}>
                {/* 単価そのものに testid を置く＝単価3層（04 §5）とスナップショット（FR-14）の
                    E2E が「行のどこかに数字がある」ではなく**単価**を見られるように */}
                <span data-testid={`item-price-${i.line_no}`}>
                  <Money value={i.unit_price} />
                </span>
                {/* 04 §5：どう決まった単価か＝定価のまま／個別単価／手入力 が読める */}
                {i.price_source && i.price_source !== "kake" && (
                  <span style={{ display: "block", fontSize: 11, color: "var(--kz-color-ink-muted)" }}>
                    {PRICE_SOURCE_LABEL[i.price_source] ?? i.price_source}
                  </span>
                )}
              </Td>
              <Td style={{ whiteSpace: "nowrap" }}>
                <Money value={i.amount} />
              </Td>
            </tr>
          );
        })}
        {/* FR-131・D-90：配送料を「商品計」と分けて出す＝**職人がカート・完了画面で見た内訳と同じ形**。
            1つの合計に畳むと「職人が見た金額と卸が見る金額が違う」に見え、問い合わせのたびに
            突き合わせられない。**「込み」（delivery_fee が NULL）の卸には何も出さない**＝
            「配送料 0円」と書くと別途かかる卸と見分けが付かず「無料」と読める。 */}
        {detail.order.delivery_fee !== null && (
          <>
            <tr data-testid="amount-items">
              {splitMode && <Td>{null}</Td>}
              <Td style={{ color: "var(--kz-color-ink-muted)" }}>商品計</Td>
              <Td>{null}</Td>
              <Td>{null}</Td>
              <Td>{null}</Td>
              <Td style={{ whiteSpace: "nowrap" }}>
                <Money value={detail.total_amount} />
              </Td>
            </tr>
            <tr data-testid="amount-delivery-fee">
              {splitMode && <Td>{null}</Td>}
              <Td style={{ color: "var(--kz-color-ink-muted)" }}>配送料</Td>
              <Td>{null}</Td>
              <Td>{null}</Td>
              <Td>{null}</Td>
              <Td style={{ whiteSpace: "nowrap" }}>
                <Money value={detail.order.delivery_fee} />
              </Td>
            </tr>
          </>
        )}
        <tr data-testid="amount-total">
          {splitMode && <Td>{null}</Td>}
          <Td style={{ fontWeight: 700 }}>合計</Td>
          <Td>{null}</Td>
          <Td>{null}</Td>
          <Td>{null}</Td>
          <Td style={{ fontWeight: 700, whiteSpace: "nowrap" }}>
            <Money value={orderGrandTotal(detail.total_amount, detail.order.delivery_fee)} />
          </Td>
        </tr>
      </Table>
      {confirm && (
        <ConfirmDialog
          title="選んだ明細を別注文に分けますか？"
          body={
            <>
              選んだ <span className="kz-num">{selected.size}</span> 明細を、新しい受付番号の別注文に切り出します。
              残りの明細はこの注文に残ります。どちらも別々に確認・承認できます。
            </>
          }
          confirmLabel="別注文に分割"
          busy={split.isPending}
          onConfirm={() => split.mutate()}
          onCancel={() => setConfirm(false)}
        />
      )}
    </Card>
  );
}

const PRICE_SOURCE_LABEL: Record<string, string> = {
  list: "定価",
  override: "個別単価",
  manual: "手入力",
  unresolved: "単価未登録",
};

/** 進捗タイムライン（D-41 の卸側）＝**誰がいつ**（order_events そのもの）。 */
function Timeline({ detail }: { detail: Detail }) {
  const index = CONSOLE_STEPS.indexOf(detail.order.status_cache);
  return (
    <Card>
      <h2 style={{ margin: "0 0 var(--kz-space-4)", fontSize: "var(--kz-font-size-md)" }}>進捗</h2>
      <ProgressBar steps={CONSOLE_STEPS} currentIndex={index} />
      <ol style={{ margin: "var(--kz-space-5) 0 0", padding: 0, listStyle: "none", fontSize: "var(--kz-font-size-sm)" }}>
        {detail.events.map((e) => (
          <li key={e.id} style={{ display: "flex", gap: "var(--kz-space-3)", padding: "var(--kz-space-1) 0" }}>
            <span className="kz-num" style={{ color: "var(--kz-color-ink-muted)", whiteSpace: "nowrap" }}>
              {formatDateTime(e.at)}
            </span>
            <span>{EVENT_LABEL[e.type] ?? e.type}</span>
            <span style={{ marginLeft: "auto", color: "var(--kz-color-ink-muted)" }}>
              {/* T-D25：職人の行だけ実名にする（1注文＝1接続なので、注文の発注者をそのまま出せる）。
                  名前が1つも取れないときは従来どおり「取引先」に戻す。
                  卸スタッフ側（tenant_user）の実名化は T-D25 のスコープ外＝触らない。 */}
              {/* **役割の列であることを保つ**＝名前だけにすると、自社の担当者なのか相手先なのかが
                  読めなくなる（「担当者／AI／システム」と人名が同じ列に混ざる）。 */}
              {e.actor_type === "builder" && detail.orderer_name
                ? `${ACTOR_LABEL.builder} ${detail.orderer_name}`
                : ACTOR_LABEL[e.actor_type]}
            </span>
          </li>
        ))}
      </ol>
    </Card>
  );
}

const EVENT_LABEL: Record<string, string> = {
  received: "受付",
  drafted: "AI が下書きを作成", // LC-01：AI の出力には常に「AI下書き」と分かる語を付ける
  draft_failed: "AI の下書きに失敗（原文のまま受付）",
  approved: "承認",
  rejected: "差戻し",
  cancelled: "取消",
  ship_instructed: "出荷指示",
  out_for_delivery: "配送に出発",
  delivered: "納品完了",
  split: "明細を分割", // FR-28：タイムラインに生の "split" を出さない
};

const ACTOR_LABEL: Record<string, string> = {
  system: "システム",
  ai: "AI",
  tenant_user: "担当者",
  builder: "取引先",
  ops: "運営",
};
